import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../static/styles/WeeklyTournaments.module.css';
import calendarIcon from '../static/img/Home/calendarIcon.svg';
import { fetchWeeklyTournaments, fetchEslTournaments, eslTournamentCheckin, eslTournamentMatchCheckin } from '../api/tournaments';

import pubgIcon from '../static/img/DailyGamingTasks/iconPubgDailyTasks.png';
import apexIcon from '../static/img/DailyGamingTasks/iconApexDailyTasks.avif';
import codIcon from '../static/img/DailyGamingTasks/iconCODDailyTasks.avif';
import freeFireIcon from '../static/img/DailyGamingTasks/iconFreeFireDailyTasks.avif';
import MessagePopup from '../components/MessagePopup';
import CannonClashInstructions from '../components/CannonClashInstructions';
import AimTrainInstructions from '../components/AimTrainInstructions';
import popupStyles from '../static/styles/MessagePopup.module.css';
import cannonClashCover from '../static/img/MadeByESL/coverCannonClash.avif';
import aimTrainCover from '../static/img/MadeByESL/coverAimTrainTour.avif';

// Import static background images
import pubgBg from '../static/img/WeeklyTournaments/imagePubgTour.avif';
import freeFireBg from '../static/img/WeeklyTournaments/imageFreeFireTour.avif';
import { jwtDecode } from 'jwt-decode';
import { trackTournamentSignup } from '../utils/analytics';

const getGameIcon = (gameName) => {
    switch (gameName.toLowerCase()) {
        case 'pubg mobile':
            return pubgIcon;
        case 'freefire':
            return freeFireIcon;
        default:
            return null;
    }
};

const getGameBackground = (gameName) => {
    switch (gameName.toLowerCase()) {
        case 'pubg mobile':
            return pubgBg;
        case 'freefire':
            return freeFireBg;
        default:
            return null;
    }
};

const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return { date: 'N/A', time: 'N/A' };
    const [datePart, timePart, ampm] = dateTimeString.split(' ');
    return {
        date: datePart,
        time: `${timePart} ${ampm}`
    };
};

const formatMatchTime = (dateTimeString) => {
    if (!dateTimeString) return { date: 'N/A', time: 'N/A' };
    const [datePart, timePart] = dateTimeString.split(' ');
    const [hours, minutes] = timePart.split(':');
    const hour24 = parseInt(hours);
    const hour12 = hour24 % 12 || 12;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    return {
        date: datePart,
        time: `${hour12}:${minutes} ${ampm}`
    };
};

const WeeklyTournaments = () => {
    const [tournaments, setTournaments] = useState([]);
    const [eslTournaments, setEslTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showRoomPopup, setShowRoomPopup] = useState(false);
    const [roomId, setRoomId] = useState('');
    const [selectedTournament, setSelectedTournament] = useState(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [showInstructions, setShowInstructions] = useState(false);
    const [showAimTrainInstructions, setShowAimTrainInstructions] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Tournaments | Ufone ESG"

        const accessToken = localStorage.getItem('access_token');
        const msisdn = localStorage.getItem('msisdn');
        setIsLoggedIn(!!accessToken && !!msisdn);

        const loadTournaments = async () => {
            try {
                setLoading(true);
                setError(null);
                const [weeklyData, eslData] = await Promise.all([
                    fetchWeeklyTournaments(),
                    fetchEslTournaments()
                ]);
                setTournaments(weeklyData);
                setEslTournaments(eslData);
            } catch (err) {
                setError(err.message || 'Failed to load tournaments');
            } finally {
                setLoading(false);
            }
        };

        loadTournaments();
    }, []);

    if (loading) {
        return <div className={styles.loading}>Loading tournaments...</div>;
    }

    if (error) {
        return <div className={styles.error}>Error: {error}</div>;
    }

    const handleJoinTournament = (tournamentId) => {
        navigate(`/join-tournament?tournamentId=${tournamentId}`);
    };

    const handleEslTournamentJoin = async (tournament) => {
        if (!isLoggedIn) {
            setPopupMessage('Please log in to participate in tournaments');
            setPopupOpen(true);
            return;
        }

        if (isLoggedIn && tournament.registered === 1 && tournament.is_running !== 1) {
            return;
        }

        try {
            if (tournament.is_running === 1) {
                setSelectedTournament(tournament);
                setShowRoomPopup(true);
                return;
            }

            const response = await eslTournamentCheckin(tournament.tourn_id);

            if (response.data.already_submited === true) {
                if (tournament.is_running === 1) {
                    setSelectedTournament(tournament);
                    setShowRoomPopup(true);
                } else {
                    setPopupMessage('You are already registered for this tournament. Wait for it to start.');
                    setPopupOpen(true);
                }
            } else {
                trackTournamentSignup(tournament.tourn_name, tournament.tourn_id, 'ESL', tournament.tournament_game);

                const isCannonClash = tournament.tournament_game === "cannon_clash_001";
                if (isCannonClash) {
                    setPopupMessage('Successfully joined the tournament! Check the instructions below for how to play.');
                    setPopupOpen(true);
                    setTimeout(() => {
                        setShowInstructions(true);
                    }, 1500);
                } else {
                    setPopupMessage('Successfully joined the tournament!');
                    setPopupOpen(true);
                }
            }
        } catch (error) {
            setPopupMessage(error.message || 'Failed to join tournament');
            setPopupOpen(true);
        }
    };

    const handleRoomIdSubmit = async () => {
        if (!roomId.trim() || !selectedTournament) return;

        try {
            const response = await eslTournamentMatchCheckin(selectedTournament.tourn_id, roomId.trim());

            // Check for both 'success' and 'successss' (typo in API)
            const isSuccess = response.success === true || response.successss === true;

            if (!isSuccess) {
                setPopupMessage(response.message || 'Failed to check in to match');
                setPopupOpen(true);
            } else {
                const gameRoute = selectedTournament.tournament_game === "aim_train_001" ? '/aim-train' : '/cannon-clash';
                navigate(gameRoute, {
                    state: {
                        roomId: roomId.trim(),
                        tournamentId: selectedTournament.tourn_id,
                        startGame: response.start_game,
                        tournamentMode: true,
                        roundNumber: response.round_number
                    }
                });
            }
        } catch (error) {
            setPopupMessage(error.message || 'Failed to check in to match');
            setPopupOpen(true);
        } finally {
            setShowRoomPopup(false);
            setRoomId('');
            setSelectedTournament(null);
        }
    };

    const handleRoomPopupClose = () => {
        setShowRoomPopup(false);
        setRoomId('');
        setSelectedTournament(null);
    };

    const handlePopupClose = () => {
        setPopupOpen(false);
    };

    const handleInstructionsClose = () => {
        setShowInstructions(false);
    };

    const handleAimTrainInstructionsClose = () => {
        setShowAimTrainInstructions(false);
    };

    const handleShowInstructions = (tournament) => {
        if (tournament.tournament_game === "cannon_clash_001") {
            setShowInstructions(true);
        } else if (tournament.tournament_game === "aim_train_001") {
            setShowAimTrainInstructions(true);
        }
    };

    const liveEslTournaments = eslTournaments.filter(t => t.is_live === 1);

    return (
        <div className={styles.container}>
            <h2 className={styles.heading}>Weekly Tournaments</h2>
            <p className={styles.subheading}>Compete in tournaments to win credits</p>

            {liveEslTournaments.length > 0 && (
                <div className={styles.eslSection}>
                    <div className={styles.sectionSeparator}>
                        <span className={styles.sectionSeparatorText}>ESG Tournaments</span>
                    </div>
                    <div className={styles.cardGrid}>
                        {liveEslTournaments.map((tournament) => (
                            <div key={tournament.Id} className={styles.cardContainer}>
                                <div className={styles.card}>
                                    <div
                                        className={styles.cardContent}
                                        style={{
                                            backgroundImage: tournament.tournament_game === "cannon_clash_001"
                                                ? `url(${cannonClashCover})`
                                                : tournament.tournament_game === "aim_train_001"
                                                    ? `url(${aimTrainCover})`
                                                    : tournament.thumbnail
                                                        ? `url(${tournament.thumbnail})`
                                                        : 'none'
                                        }}
                                    >
                                        {tournament.status === 4 ? (
                                            <div className={styles.completedOverlay}>
                                                <h3>Tournament has ended</h3>
                                            </div>
                                        ) : (
                                            <>
                                                {isLoggedIn && tournament.registered === 1 && tournament.match_time && (
                                                    <div className={styles.matchTimeCorner}>
                                                        <p>Match Time</p>
                                                        <p>{formatMatchTime(tournament.match_time).date}</p>
                                                        <p>{formatMatchTime(tournament.match_time).time}</p>
                                                    </div>
                                                )}

                                                {isLoggedIn && tournament.registered === 1 && tournament.room_id && (
                                                    <div className={styles.roomIdCorner}>
                                                        <p>Room ID</p>
                                                        <p>{tournament.room_id}</p>
                                                    </div>
                                                )}

                                                {tournament.is_running === 1 && (
                                                    <div className={styles.liveBadge}>
                                                        <span className={styles.liveDot} />
                                                        LIVE
                                                    </div>
                                                )}
                                                <div className={styles.bottomContent}>
                                                    <div className={styles.timeInfo}>
                                                        <img src={calendarIcon} alt="Calendar" />
                                                        <div>
                                                            <p>{formatDateTime(tournament.time).date}</p>
                                                            <p>{formatDateTime(tournament.time).time}</p>
                                                        </div>
                                                    </div>
                                                    <div className={styles.rightInfo}>
                                                        <div className={styles.prizes}>
                                                            {(tournament.entryFee != null || tournament.isEntryFree != null) && (
                                                                <p className={styles.entryFee}>
                                                                    {tournament.entryFee != null
                                                                        ? `Entry: ${tournament.entryFee} Credits`
                                                                        : tournament.isEntryFree === 1 ? 'Free Entry' : 'N/A'}
                                                                </p>
                                                            )}
                                                            {tournament.prize && <p>Reward: {tournament.prize} Credits</p>}
                                                            {tournament.total_checkin && <p>Participants: {tournament.total_checkin}</p>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <button
                                    className={styles.joinButton}
                                    onClick={() => handleEslTournamentJoin(tournament)}
                                    style={{ display: tournament.status === 4 ? 'none' : 'block' }}
                                >
                                    {!isLoggedIn ? 'Login Required' : (tournament.is_running === 1 ? 'Play Now' : (isLoggedIn && tournament.registered === 1) ? 'Registered' : 'Join now')}
                                </button>

                                <div className={styles.actionButtons}>
                                    {tournament.is_running === 1 && (
                                        <button
                                            className={styles.detailsButtonBelow}
                                            onClick={() => navigate(`/tournament/${tournament.tourn_id}`)}
                                            title="View Tournament Details"
                                        >
                                            <span className={styles.detailsText}>Leaderboard</span>
                                        </button>
                                    )}
                                    {(tournament.tournament_game === "cannon_clash_001" || tournament.tournament_game === "aim_train_001") && tournament.status !== 4 && (
                                        <button
                                            className={styles.infoButtonBelow}
                                            onClick={() => handleShowInstructions(tournament)}
                                            title="View Tournament Instructions"
                                        >
                                            <span className={styles.infoText}>How to Play</span>
                                            <span className={styles.infoIcon}>?</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {eslTournaments.length > 0 && tournaments.length > 0 && (
                <div className={styles.sectionSeparator}>
                    <span className={styles.sectionSeparatorText}>Weekly Tournaments</span>
                </div>
            )}

            <div className={styles.cardGrid}>
                {tournaments.map((tournament) => {
                    const { date, time } = formatDateTime(tournament.time);
                    const isCompleted = tournament.status !== 1 || tournament.is_running === 1;
                    const gameIconSrc = getGameIcon(tournament.tourn_name);
                    const gameBgSrc = getGameBackground(tournament.tourn_name);

                    return (
                        <div key={tournament.id} className={styles.cardContainer}>
                            <div className={styles.card}>
                                <div
                                    className={styles.cardContent}
                                    style={{ backgroundImage: gameBgSrc ? `url(${gameBgSrc})` : 'none' }}
                                >
                                    {isCompleted ? (
                                        <div className={styles.completedOverlay}>
                                            <h3>Tournament has ended</h3>
                                        </div>
                                    ) : (
                                        <div className={styles.bottomContent}>
                                            <div className={styles.timeInfo}>
                                                <img src={calendarIcon} alt="Calendar" />
                                                <div>
                                                    <p>{date}</p>
                                                    <p>{time}</p>
                                                </div>
                                            </div>
                                            <div className={styles.rightInfo}>
                                                <div className={styles.prizes}>
                                                    <p className={styles.entryFee}>
                                                        {tournament.entryFee != null
                                                            ? `Entry: ${tournament.entryFee} Credits`
                                                            : tournament.isEntryFree === false ? 'N/A' : 'Free Entry'}
                                                    </p>
                                                    {tournament.prize && <p>1st: {tournament.prize} Credits</p>}
                                                    {tournament.second_prize && <p>2nd: {tournament.second_prize} Credits</p>}
                                                    {tournament.third_prize && <p>3rd: {tournament.third_prize} Credits</p>}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <button
                                className={styles.joinButton}
                                onClick={() => handleJoinTournament(tournament.tourn_id)}
                            >
                                Join now
                            </button>
                        </div>
                    );
                })}
            </div>

            {showRoomPopup && (
                <div className={popupStyles.popupOverlay}>
                    <div className={popupStyles.popupContent}>
                        <h3>Enter Room ID</h3>
                        <p>You are already checked in to this tournament. Please enter your room ID to continue:</p>
                        <input
                            type="text"
                            value={roomId}
                            onChange={(e) => setRoomId(e.target.value)}
                            placeholder="Enter room ID"
                            className={popupStyles.roomInput}
                        />
                        <div className={popupStyles.popupButtons}>
                            <button onClick={handleRoomIdSubmit} className={popupStyles.submitButton}>
                                Submit
                            </button>
                            <button onClick={handleRoomPopupClose} className={popupStyles.cancelButton}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <MessagePopup
                isOpen={popupOpen}
                message={popupMessage}
                onClose={handlePopupClose}
            />

            <CannonClashInstructions
                isOpen={showInstructions}
                onClose={handleInstructionsClose}
            />

            <AimTrainInstructions
                isOpen={showAimTrainInstructions}
                onClose={handleAimTrainInstructionsClose}
            />
        </div>
    );
};

export default WeeklyTournaments;
